/**
 * @loci/types - Shared TypeScript types for LOCI applications
 * @packageDocumentation
 */

// Export all common types
export * from './common';

// Export auth types
export * from './auth';

// Export identity types
export * from './identity';

// Export saved-search wire types (FRE-926)
export * from './saved-search';

// Export detailed event types (with aliases to avoid conflicts)
export type {
  Event as DetailedEvent,
  CreateEvent,
  DisplayEvent,
  EventValidationResult,
  DataQualityMetrics,
  DuplicateDetectionResult,
  ManualReviewItem,
  Venue as DetailedVenue,
  Artist,
  ArtistSocial,
  ImageUrls,
  DescriptionAttribution,
  VenueType,
  EventStatus,
} from './event';

// Runtime value export: canonical lifecycle-status tuple (single source of
// truth for the EventStatus union and the createEventSchema Zod enum).
export { EVENT_LIFECYCLE_STATUSES } from './event';

// Export badge types
export * from './badge';
export * from './badge-registry';

// Export action types (with UserRole aliased to avoid conflict with auth)
export {
  ActionType,
  EventState,
  Platform,
  CalendarProvider,
  UserRole as ActionUserRole,
  type EventContext,
  type UserContext,
  type DisplayContext,
  type ActionAvailability,
  type SecondaryAction,
  type PrimaryAction,
  type ActionLayout,
  type ActionResult,
} from './actions';

// Re-export commonly used types for convenience
export type {
  User,
  Event,
  Venue,
  EventCategory,
  Coordinates,
  Location,
  ApiResponse,
  PaginatedResponse,
} from './common';

export type {
  AuthError,
  AuthErrorCode,
  ClientType,
  JwtPayload,
  RefreshToken,
  Session,
  AuthContext,
  ReactAuthContext,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
} from './auth';

export type {
  ValidationError,
  SearchParams,
  HealthCheck,
  HttpMethod,
  RequestConfig,
  ApiEndpoint,
  ValidationErrorResponse,
  SearchResponse,
  FileUploadRequest,
  FileUploadResponse,
  ServiceHealth,
  RateLimitInfo,
  WebhookEvent,
  WebhookResponse,
} from './api/index';

// Export generated OpenAPI types
export type {
  paths as BackendApiPaths,
  components as BackendApiComponents,
} from './generated/backend-api';
export type {
  paths as GeoCoreApiPaths,
  components as GeoCoreApiComponents,
} from './generated/geo-core';
export type {
  paths as DataPipelineApiPaths,
  components as DataPipelineApiComponents,
} from './generated/data-pipeline-v1';
export type {
  paths as AffiliateCheckoutApiPaths,
  components as AffiliateCheckoutApiComponents,
} from './generated/affiliate-checkout-v1';

// Export helper utilities for extracting types from OpenAPI schemas
export type { QueryParams, ResponseBody, RequestBody } from './helpers';

// Export timezone utilities
export * from './timezone';

// Spatial search response shapes (API-1182 / map search)
export type {
  SpatialSearchResponse,
  SpatialSearchResult,
  SpatialSearchPagination,
  SpatialSearchMetadata,
} from './spatial';

// Saved events API response shapes (FRE-626)
export type {
  SavedEventPayload,
  SavedEventApiItem,
  SavedEventsPagination,
  SavedEventsApiResponse,
  SavedEventCoordinate,
} from './saved-events';

// Recommendation surface types (FRE-772, API-1409)
export type { FriendsGoingPreview, TopMatchEvent } from './recommendations';

// Discover surface view-model (WEB-46)
export type {
  DiscoverEvent,
  DiscoverBadge,
  DiscoverEventStatus,
  DiscoverFacetCounts,
  DiscoverFacetScopeKey,
  // API-1679 — canonical facet-dimension union, shared with the backend
  // producer (`discoverFacets.ts` re-exports it) so `facets.degraded` and the
  // `exclude` passes can never name different dimensions.
  DiscoverFacetDimension,
  // WEB-832 — one genre facet bucket: URL key + source-published label + count.
  DiscoverGenreFacet,
  DiscoverPriceBucket,
  DiscoverVenueKind,
  DiscoverAuthenticatedEnvelope,
  DiscoverEventPersonalizationProjection,
  DiscoverFollowedArtist,
  DiscoverFollowedVenue,
  DiscoverPickedForYou,
  DiscoverPickedForYouFriendsGoing,
  DiscoverSavedSearch,
  DiscoverTasteChip,
  DiscoverTasteProfile,
  // API-1671 — canonical `meta` envelope wire contract (shared with `@loci/api`).
  DiscoverMeta,
} from './discover';

// PIP-87 — venue-kind discriminator helpers. Shared between the data-pipeline
// persister, the backend Discover route, and the category-hub top-picks ranker
// so callers don't have to inline the wire/Prisma identifier mapping.
export {
  DISCOVER_VENUE_KINDS,
  isResolvedVenue,
  isUnresolvedVenue,
  VENUE_KIND_PRISMA_IDENTIFIERS,
  venueKindFromPrismaIdentifier,
} from './discover';
export type { VenueKindPrismaIdentifier } from './discover';

// FRE-928 — structured per-event "why" reason explainability. `WhyReason` is
// the wire shape carried on `DiscoverEvent.whyReasons`; `WhyReasonContribution`
// + `WhyReasonCode` back the backend scorer that produces them.
export type { WhyReason, WhyReasonContribution, WhyReasonCode } from './why-reasons';

// WEB-36 — sponsored-listing wire contracts. `SponsoredEventMeta` is the
// annotation `injectSponsoredSlots` (API-1361) attaches to a promoted event on
// `DiscoverEvent.sponsored` and the `/recommendations/happening-now` response.
export type { SponsoredWireTier, SponsoredEventMeta, SponsoredHeroSlot } from './sponsored';
export { SPONSORED_DISCLOSURE_LABEL, SPONSORED_DISCLOSURE_URL } from './sponsored';

// API-1616 — the server-derived CTA contract (spec §2.3 / D6). Carried on
// `DiscoverEvent`, the event detail payload, and the feed rec mappers so no
// surface re-derives "which button" from `ticketUrl` alone.
export type { CtaType, EventCta } from './cta';
export { RESALE_CTA_LABEL } from './cta';

// Canonical vibe slugs (PIP-80) — shared between the web `/discover` filter
// and the data-pipeline `deriveVibes()` classifier.
export {
  VIBE_ORDER,
  VALID_VIBES,
  VIBE_QUERY_PARAM,
  VIBE_LABELS,
  FREE_VIBE,
  FREE_PRICE_RANGE,
  asVibeSlug,
} from './discover/vibes';
export type { VibeSlug } from './discover/vibes';

// Source-published genre filter keys (WEB-832) — the deliberate URL-key /
// display-label split for `Event.sourceGenre`, shared by the `/events/discover`
// facet producer and every client that renders or links a genre.
export {
  GENRE_QUERY_PARAM,
  GENRE_FILTER_KEY_MAX_LENGTH,
  PLACEHOLDER_GENRE_FILTER_KEYS,
  genreFilterKey,
  isGenreFilterKey,
  isPlaceholderGenreKey,
} from './discover/genres';

// COR-125 — the reserved "Greater <City>" catch-all neighborhood bucket:
// events / venues outside every neighborhood, as one more entry on the
// neighborhood surfaces so their counts reconcile with the headline total.
export {
  ELSEWHERE_NEIGHBORHOOD_SLUG,
  formatElsewhereNeighborhoodLabel,
  isElsewhereNeighborhoodSlug,
} from './discover/neighborhoods';

// Ingestion pipeline normalized event DTO (PIP-8)
export {
  EVENT_CATEGORIES as NORMALIZED_EVENT_CATEGORIES,
  EVENT_STATUSES as NORMALIZED_EVENT_STATUSES,
  PLATFORM_EVENT_CATEGORIES,
  type NormalizedEvent,
  type NormalizedEventCategory,
  type NormalizedEventStatus,
  type PlatformEventCategory,
} from './ingestion/normalized-event';

// Closed category taxonomy (COR-37)
export {
  AUDIENCE_FACET_NAMESPACE,
  AUDIENCE_FACET_VALUES,
  audienceFacetToken,
  CANONICAL_CATEGORY_LABELS,
  CANONICAL_CATEGORY_SLUGS,
  CATEGORY_AUTO_LIVE_CONFIDENCE,
  CATEGORY_TAG_VOCABULARY,
  FORMAT_FACET_NAMESPACE,
  FORMAT_FACET_VALUES,
  formatFacetToken,
  isCanonicalCategorySlug,
  isCategoryTag,
  isRetiredCategorySlug,
  mapToCanonicalCategory,
  OLD_TO_NEW_CATEGORY,
  OPERATOR_CONFIRMED_ASSIGNMENT_METHOD,
  OPERATOR_CONFIRMED_CONFIDENCE,
  RETIRED_CATEGORY_SLUGS,
  type AudienceFacetToken,
  type AudienceFacetValue,
  type CanonicalCategorySlug,
  type CategoryTag,
  type FormatFacetToken,
  type FormatFacetValue,
} from './ingestion/category-taxonomy';

// Social-graph API response shapes (FRE-777)
export type {
  FollowingUser,
  SocialPagination,
  UserFollowingResponse,
  UserFollowersResponse,
  FollowUserResponse,
  TicketPurchase,
  UserTicketsResponse,
} from './social';

// ---------------------------------------------------------------------------
// Event data-quality vocabulary (COR-16). Machine-readable reason codes,
// severity classification, and the QualityStatus string-union mirror of the
// Prisma enum. Keep quality exports in this contiguous block at the end.
// ---------------------------------------------------------------------------
export { QUALITY_REASON_SEVERITY, QUALITY_REASON_CODES } from './quality';
export type {
  QualityReasonCode,
  QualitySeverity,
  QualityStatus,
  QualitySubscores,
} from './quality';

// --- COR-17: data-quality scorer INPUT view --------------------------------
export type { ScorableEvent } from './scorable-event';
export { DEAD_LINK_STATUS, LIVE_LINK_STATUS } from './link-status';
export type { LinkStatus } from './link-status';
// PIP-407 — the sources whose evergreen series are capped + kept off rails.
export { SERIES_EVERGREEN_SOURCES, isSeriesEvergreenSource } from './event-series';
export type { SeriesEvergreenSource } from './event-series';

// ---------------------------------------------------------------------------
// Apple Music shared response types (COR-14). Canonical mirror of the Apple
// Music API resource-object shapes consumed by the backend service module and
// (later, M4) the web connect component. Single source of truth — do not
// re-declare in apps or services.
// ---------------------------------------------------------------------------
export type {
  AMResourceType,
  AMResourceReference,
  AMArtwork,
  AMPlayParams,
  AMSongAttributes,
  AMSongPreview,
  AMSong,
  AMRelationship,
  AMAPIResponse,
  AMLibraryArtistAttributes,
  AMLibraryArtist,
  AMCatalogArtistAttributes,
  AMCatalogArtist,
  AMEnrichedLibraryArtist,
  LOCITasteProfile,
} from './appleMusic';

// ---------------------------------------------------------------------------
// FRE-925: explicit follow-graph API response shapes. Powers the Discover
// "Artists/Venues you follow" rails and personalized framing. Single source of
// truth — do not re-declare in apps or services.
// ---------------------------------------------------------------------------
export type {
  ArtistFollowState,
  VenueFollowState,
  FollowState,
  FollowedArtistNextShow,
  FollowedArtist,
  FollowedVenue,
  FollowedListPagination,
  FollowedArtistsResponse,
  FollowedVenuesResponse,
  MarkSeenResponse,
} from './follows';
